<template>
  <div class="offline-notification" v-if="!isOnline">
    <div class="offline-content">
      <i class="pi pi-wifi" style="color: red;"></i>
      <span>You are currently offline. Some features may be limited.</span>
    </div>
  </div>
  <div class="sync-notification" v-if="showSyncNotification">
    <div class="sync-content">
      <i class="pi pi-sync" :class="{ 'pi-spin': isSyncing }"></i>
      <span>{{ syncMessage }}</span>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted } from 'vue';
import { syncPendingData } from '../services/offline';

export default defineComponent({
  name: 'OfflineStatusNotification',
  setup() {
    const isOnline = ref(navigator.onLine);
    const showSyncNotification = ref(false);
    const isSyncing = ref(false);
    const syncMessage = ref('Syncing data...');
    
    // Handle online/offline status changes
    const handleOnline = () => {
      isOnline.value = true;
      
      // Show syncing notification
      showSyncNotification.value = true;
      isSyncing.value = true;
      syncMessage.value = 'Syncing data...';
      
      // Attempt to sync data
      syncPendingData()
        .then(success => {
          if (success) {
            syncMessage.value = 'All data synchronized!';
          } else {
            syncMessage.value = 'Sync failed. Will retry later.';
          }
          isSyncing.value = false;
          
          // Hide notification after a delay
          setTimeout(() => {
            showSyncNotification.value = false;
          }, 3000);
        })
        .catch(() => {
          syncMessage.value = 'Sync failed. Will retry later.';
          isSyncing.value = false;
          
          // Hide notification after a delay
          setTimeout(() => {
            showSyncNotification.value = false;
          }, 3000);
        });
    };
    
    const handleOffline = () => {
      isOnline.value = false;
    };
    
    onMounted(() => {
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
      window.addEventListener('app-online', handleOnline);
      window.addEventListener('app-offline', handleOffline);
    });
    
    onUnmounted(() => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('app-online', handleOnline);
      window.removeEventListener('app-offline', handleOffline);
    });
    
    return {
      isOnline,
      showSyncNotification,
      isSyncing,
      syncMessage,
    };
  }
});
</script>

<style scoped>
.offline-notification {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background-color: #fff3cd;
  color: #856404;
  z-index: 9999;
  padding: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.offline-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.sync-notification {
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  background-color: #d4edda;
  color: #155724;
  z-index: 9999;
  padding: 0.75rem 1rem;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.sync-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.pi-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>